import { isEmpty, isUndefined, join, map, noop } from 'lodash';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useState } from 'react';
import styled from 'styled-components';

import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { analytics } from '@analytics';
import { navigation } from '@constants';
import { useFeature } from '@hooks/use-feature';
import { Button } from '@components/button';
import { DefaultImage } from '@components/default/default-image';
import { Box, Flex } from '@components/grid';
import { Icon } from '@components/icons';
import { Image } from '@components/image';
import { Label, P } from '@components/typography';

const StyledImage = styled(Image)`
  width: 100%;
  height: auto;
`;
const Tag = () => {
  return (
    <Label bg="grey100" color="greyPrimary" px="0.4rem" py="0.2rem" mb="0.2rem">
      REFUNDED
    </Label>
  );
};

const PastOrder = observer(({ order, displayAsList, handleClick, showIsRefundedTag, px }) => {
  const { formattedDate, formattedOrderItems, productNamesJoined, isRefunded, highestCostItemWithImage } = order;

  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const enableReorder = useFeature('reorder');

  const handleReorder = () => {
    analytics.track(ANALYTICS_EVENTS.REORDER_CLICKED);

    if (!order.isAvailableAtCurrentRestaurant) {
      return;
    }

    order.addOrderItemsToCart();
    setIsLoading(true);
    router.push({
      pathname: navigation.MENU,
      query: 'showCart',
    });
  };

  if (isEmpty(formattedOrderItems)) {
    return null;
  }

  return (
    <Flex
      flexDirection="column"
      width="100%"
      px={isUndefined(px) ? { _: '1.5rem', md: '1rem' } : px}
      py={{ _: '1.5rem', md: '1rem' }}
      bg="white"
      mb="0.25rem"
    >
      <Flex flexDirection="row">
        <Box width="100px">
          {highestCostItemWithImage.image ? (
            <StyledImage
              src={highestCostItemWithImage.image}
              alt={highestCostItemWithImage.name}
              width={100}
              height={80}
            />
          ) : (
            <DefaultImage width="100%" height="80px" />
          )}
        </Box>

        <Flex flexDirection="column" px="1" flexGrow="1">
          {isRefunded && showIsRefundedTag && (
            <Box width="fit-content">
              <Tag>refunded</Tag>
            </Box>
          )}
          <Box
            onClick={() => {
              if (displayAsList) handleClick(order);
            }}
            style={{ cursor: displayAsList ? 'pointer' : 'text' }}
          >
            <Label>{productNamesJoined}</Label>
          </Box>
          <P variant={3} color="greyPrimary" py={{ _: '1rem', md: '0.5rem' }}>
            {formattedDate}
          </P>
          <Box>
            <Button onClick={() => setIsOpen(!isOpen)} variant="link">
              <P variant={3}>{`${isOpen ? 'Hide' : 'Show'} details`}</P>
            </Button>
          </Box>
        </Flex>

        {enableReorder && !displayAsList && (
          <Box>
            <Button isLoading={isLoading} onClick={handleReorder}>
              Reorder
            </Button>
          </Box>
        )}
        {displayAsList && (
          <Icon
            name="chevronRight"
            maxWidth="1rem"
            fill="currentColor"
            onClick={() => handleClick(order)}
            style={{ cursor: 'pointer' }}
          />
        )}
      </Flex>

      {isOpen && (
        <Flex mt={1} flexDirection="column" width="100%">
          {map(formattedOrderItems, ({ product, productChoices, productQuantity, price, formattedKilojoules }) => {
            const { name } = product;
            const choiceNamesJoined = join(
              map(productChoices, productChoice => productChoice.name),
              ', '
            );

            return (
              <Flex mb="0.75rem">
                <QuantityBorder justifyContent="center" alignItems="center">
                  <P variant={3}>{productQuantity}</P>
                </QuantityBorder>

                <Flex ml="0.75rem" flexDirection="column" flexGrow="1">
                  <Flex mb="0.25rem" justifyContent="space-between">
                    <Label>{name}</Label>
                    <P variant={3}>{price}</P>
                  </Flex>

                  <Flex justifyContent="space-between">
                    <P color="greyPrimary" variant={3}>
                      {choiceNamesJoined}
                    </P>
                    {formattedKilojoules && (
                      <P color="greyPrimary" variant={3}>
                        {formattedKilojoules}
                      </P>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
});

const QuantityBorder = styled(Flex)`
  min-width: 1.5rem;
  height: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.grey300};
`;

PastOrder.propTypes = {
  order: PropTypes.shape({
    formattedDate: PropTypes.string.isRequired,
    productNamesJoined: PropTypes.string.isRequired,
    orderItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    formattedOrderItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    formattedForCart: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    highestCostItemWithImage: PropTypes.shape({ image: PropTypes.string, name: PropTypes.string }).isRequired,
  }).isRequired,
  displayAsList: PropTypes.bool,
  showIsRefundedTag: PropTypes.bool,
  handleClick: PropTypes.func,
};

PastOrder.defaultProps = { displayAsList: false, handleClick: noop, showIsRefundedTag: false };

export { PastOrder };
